// frontend/src/pages/InvoicesInbox.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

const API_URL = process.env.REACT_APP_API_URL || "";
const HQ = process.env.REACT_APP_HQ_LABEL || "Sede";

/** Format YYYY-MM in locale (niente UTC, niente toISOString). */
function toYYYYMM(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
}

/** Ultimi 12 mesi: value in YYYY-MM, label localizzata. */
function buildLastMonths() {
  const out = [];
  const today = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
    out.push({
      value: toYYYYMM(d), // <-- niente toISOString()
      label: d.toLocaleDateString(undefined, { year: "numeric", month: "long" }),
    });
  }
  return out;
}

const fmtMoney = (v) =>
  Number(v || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const fmtDateIT = (value) => {
  if (!value) return "";
  const s = String(value).slice(0, 10);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString("it-IT");
};

const authHeaders = () => {
  const token = localStorage.getItem("access") || "";
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function InvoicesInbox() {
  const nav = useNavigate();

  // Costruisci i mesi una sola volta
  const months = useMemo(() => buildLastMonths(), []);
  const [month, setMonth] = useState(() => (months[0] ? months[0].value : toYYYYMM(new Date())));

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const cards = useMemo(() => {
    const list = rows || [];
    const total = list.reduce((acc, r) => acc + Number(r.total || 0), 0);
    const unpaid = list.filter((r) => !(r.is_paid || String(r.status || "").toUpperCase() === "PAID")).length;
    return { total, count: list.length, unpaid };
  }, [rows]);

  // Carica lista quando cambia il mese
  useEffect(() => {
    let abort = false;
    (async () => {
      setLoading(true);
      try {
        const rl = await fetch(`${API_URL}/api/fatture/inbox/?month=${month}`, {
          headers: { ...authHeaders() },
        });
        const arr = rl.ok ? await rl.json() : [];
        if (!abort) setRows(
              Array.isArray(arr)
                ? arr
                : Array.isArray(arr?.results)
                ? arr.results
                : []
            );
      } catch {
        if (!abort) setRows([]);
      }

      if (!abort) setLoading(false);
    })();

    return () => {
      abort = true;
    };
  }, [month]);

  const downloadZip = async () => {
    try {
      const r = await fetch(`${API_URL}/api/fatture/inbox/export-zip/?month=${month}`, {
        headers: { ...authHeaders() },
      });
      if (!r.ok) throw new Error("Errore download");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `proforme_inbox_${month}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert("Impossibile scaricare lo ZIP.");
    }
  };

  
const statoLabel = (s) => {
  const up = (s || "").toUpperCase();
  if (up === "DRAFT") return "Bozza";
  if (up === "ISSUED") return "Proforma";
  if (up === "SENT") return "Inviata";
  if (up === "PAID") return "Pagata";
  return s;
};

const openPdf = async (row) => {
    try {
      const pdfUrl = row.pdf_url || `/api/fatture/${row.id}/pdf/`;
      const r = await fetch(`${API_URL}${pdfUrl}`, { headers: { ...authHeaders() } });
      if (!r.ok) throw new Error();
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      alert("Impossibile aprire il PDF.");
    }
  };

  return (
    <div className="container mt-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="mb-0">🧾 Proforme ricevute dalla {HQ}</h2>
        <div className="d-flex gap-2">
          <button className="btn btn-outline-secondary" onClick={() => nav("/")}>
            ↩︎ Menu principale
          </button>
          <button className="btn btn-outline-primary" onClick={() => nav("/fatture/archivio")}>
            Archivio
          </button>
        </div>
      </div>

      <div className="card shadow-sm">
        <div className="card-header d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-2">
            <span>Mese:</span>
            <select
              className="form-select form-select-sm"
              style={{ width: 240 }}
              value={month}
              onChange={(e) => setMonth(e.target.value)}
            >
              {months.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
          </div>
          <button className="btn btn-sm btn-outline-secondary" onClick={downloadZip}>
            Esporta ZIP
          </button>
        </div>

        <div className="card-body">
          <div className="row g-3 mb-3">
            <div className="col-12 col-md-4">
              <div className="card h-100 shadow-sm">
                <div className="card-body">
                  <div className="text-muted small">Totale (mese)</div>
                  <div className="display-6">€ {fmtMoney(cards.total)}</div>
                </div>
              </div>
            </div>
            <div className="col-12 col-md-4">
              <div className="card h-100 shadow-sm">
                <div className="card-body">
                  <div className="text-muted small">N° proforme</div>
                  <div className="display-6">{cards.count}</div>
                </div>
              </div>
            </div>
            <div className="col-12 col-md-4">
              <div className="card h-100 shadow-sm">
                <div className="card-body">
                  <div className="text-muted small">Da pagare</div>
                  <div className="display-6">{cards.unpaid}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="table-responsive">
            <table className="table table-sm mb-0 align-middle">
              <thead>
                <tr>
                  <th>Serie/N.</th>
                  <th>Data</th>
                  <th>Cliente</th>
                  <th className="text-end">Totale</th>
                  <th>Stato</th>
                  <th className="text-end">PDF</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} className="text-center text-muted">
                      Caricamento…
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center text-muted">
                      Nessuna proforma per il mese selezionato.
                    </td>
                  </tr>
                ) : (
                  rows.map((r) => (
                    <tr key={r.id}>
                      <td>{`${r.series || ""}/${r.number || ""}`}</td>
                      <td>{fmtDateIT(r.date)}</td>
                      <td>{r.fatt_ragione_sociale || "—"}</td>
                      <td className="text-end">€ {fmtMoney(r.total)}</td>
                      <td>
  <span className={`badge ${
    r.is_paid ? "bg-success" :
    (r.status || "").toUpperCase() === "ISSUED" ? "bg-warning text-dark" :
    (r.status || "").toUpperCase() === "SENT" ? "bg-info text-dark" :
    "bg-secondary"
  }`}>
    {r.is_paid ? "Pagata" : statoLabel(r.status)}
  </span>
</td>
                      <td className="text-end">
                        <button className="btn btn-sm btn-outline-primary" onClick={() => openPdf(r)}>
                          PDF
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
